import { useEffect, useState } from 'react';
import { Modal } from '../../../utils/Modal';
import { dashboardService } from '@services/dashboardService';
import type { Dashboard, ItemForGetPeopleListToSP } from '../../../../types/dashboard';

interface ConfigSPModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  dashboard?: Dashboard;
  projectId?: number | null;
}

interface tableContentProps {
  original: ItemForGetPeopleListToSP[];
  sorted: ItemForGetPeopleListToSP[];
}

export const ConfigSpModal: React.FC<any> = ({
  open,
  setOpen,
  dashboard,
  projectId,
}: ConfigSPModalProps) =>
{
  const [isLoading, setIsLoading] = useState(false);
  const [tableContent, setTableContent] = useState<tableContentProps>({ original: [], sorted: [] });

  //sort
  const [tableSortedBy, setTableSortedBy] = useState<string>(''); //'name'|'sp'|''
  const [sortingDirection, setSortingDirection] = useState<string>(''); //'asc'|'desc'|''
  const [personIdToEdit, setPersonIdToEdit] = useState<number>();
  const [newSPToEdit, setNewSPToEdit] = useState<number>();

  useEffect(() =>
  {
    setIsLoading(true);
    loadingContent();
  }, [open]);

  const loadingContent = async () =>
  {
    const response: ItemForGetPeopleListToSP[] = dashboard
      ? await dashboardService.GetListPeopleByProjectId(dashboard.ProjectId, dashboard.DashboardId)
      : await dashboardService.GetListPeopleByProjectId(projectId, undefined);

    setIsLoading(false);
    setTableContent({ original: response, sorted: response });
  };

  const sortTable = (columnName: string) =>
  {
    let sortDirection: string = '';
    if (
      columnName === 'default' ||
      columnName === undefined ||
      columnName === null ||
      columnName === ''
    )
    {
      columnName = 'default';
      sortDirection = '';
    }
    else
    {
      if (tableSortedBy === columnName && sortingDirection === 'asc')
      {
        sortDirection = 'desc';
      }
      else if (tableSortedBy === columnName && sortingDirection === 'desc')
      {
        return sortTable('default');
      }
      else
      {
        sortDirection = 'asc';
      }
    }

    const sortedOriginal: ItemForGetPeopleListToSP[] = sortByColumn(columnName, sortDirection);
    setSortingDirection(sortDirection);
    setTableSortedBy(columnName);
    setTableContent({ ...tableContent, sorted: sortedOriginal });
  };

  const sortByColumn = (columnName: string, sortingDirection: string) =>
  {
    let sortedTable = tableContent.original;
    switch (columnName)
    {
      case 'name':
        sortedTable =
          sortingDirection === 'asc'
            ? tableContent.original
              .slice()
              .sort((a, b) => (!a.Name ? -1 : !b.Name ? +1 : a.Name.localeCompare(b.Name)))
            : tableContent.original
              .slice()
              .sort((b, a) => (!a.Name ? -1 : !b.Name ? +1 : a.Name.localeCompare(b.Name)));
        break;
      case 'sp':
        sortedTable =
          sortingDirection === 'asc'
            ? tableContent.original.slice().sort((a, b) => a.SP - b.SP)
            : tableContent.original.slice().sort((b, a) => a.SP - b.SP);
        break;

      default:
        sortedTable = tableContent.original;
    }
    return sortedTable;
  };

  const setNewClockOutComment = (e: React.ChangeEvent<HTMLInputElement>) =>
  {
    if (Number(e.target.value) >= 0)
    {
      setNewSPToEdit(Number(e.target.value));
    }
  };

  const keyDownEnterInputValue = (e: React.KeyboardEvent<HTMLInputElement>) =>
  {
    if (e.key === 'Enter')
    {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleShowInputEdit = (item: ItemForGetPeopleListToSP) =>
  {
    if (personIdToEdit === item.PersonID)
    {
      savePersonSP(item);
    }
    else
    {
      setPersonIdToEdit(item.PersonID);
      setNewSPToEdit(item.SP);
    }
  };

  const savePersonSP = async (item: ItemForGetPeopleListToSP) =>
  {
    const request: ItemForGetPeopleListToSP = dashboard
      ? {
        DashboardId: dashboard.DashboardId,
        PersonID: item.PersonID,
        ProjectId: dashboard.ProjectId || 0,
        SP: newSPToEdit || 0,
      }
      : {
        PersonID: item.PersonID,
        ProjectId: projectId || 0,
        SP: newSPToEdit || 0,
      };
    const copyTable = tableContent.sorted,
      index = copyTable.findIndex(x => x.PersonID === item.PersonID);

    copyTable[index]!.SP = newSPToEdit || 0;
    setTableContent({ ...tableContent, sorted: copyTable });

    setPersonIdToEdit(0);
    setNewSPToEdit(0);
    await dashboardService.SavePersonSPProject(request);
  };

  const TableSkeleton = ({ rows = 7 }) =>
  {
    const skeletonRows = Array.from({ length: rows }, (_, index) => index);
    return (
      <div className="tables">
        <table className="table table-sm table-bordered">
          <thead>
            <tr>
              <th>
                <div className="skeleton-line skeleton-header"></div>
              </th>
              <th>
                <div className="skeleton-line skeleton-header"></div>
              </th>
              <th>
                <div className="skeleton-line skeleton-header"></div>
              </th>
            </tr>
          </thead>
          <tbody>
            {skeletonRows.map((_, index) => (
              <tr key={index}>
                <td>
                  <div className="skeleton-line skeleton-name"></div>
                </td>
                <td>
                  <div className="skeleton-line skeleton-sp"></div>
                </td>
                <td>
                  <div className="skeleton-button"></div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      title="List Employee Config SP"
      preventCloseClickOut
    >
      <div className="w-96 max-h[512px] min-h[512px]">
        <div className="pt-[10px] overflow-y-auto max-h-[60vh]">
          {!isLoading ? (
            <>
              <table className="w-full max-w-full overflow-y-auto !mb-0 bg-transparent table-bordered">
                <thead>
                  <tr>
                    <th
                      onClick={() => sortTable('name')}
                      className="text-header-table cursor-pointer"
                    >
                      <span className="pl-2 pr-0.5 ">Employees</span>
                      <i
                        className={`${tableSortedBy === 'name' ? (sortingDirection === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc') : 'fa fa-sort'} inline-block w-5`}
                      />
                    </th>
                    <th
                      onClick={() => sortTable('sp')}
                      className="text-header-table cursor-pointer"
                    >
                      <span className="pl-2 pr-0.5">SP</span>
                      <i
                        className={`${tableSortedBy === 'sp' ? (sortingDirection === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc') : 'fa fa-sort'} inline-block w-5`}
                      />
                    </th>
                    <th className="text-header-table">
                      <span className="pl-2 pr-0.5">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableContent.sorted.map(item => (
                    <tr key={item.PersonID}>
                      <td className="text-table">
                        <p>{item.Name}</p>
                      </td>
                      <td className="text-table p-0">
                        {personIdToEdit === item.PersonID ? (
                          <div className="">
                            <input
                              maxLength={5}
                              style={{ width: 50 }}
                              onChange={setNewClockOutComment}
                              onKeyDown={keyDownEnterInputValue}
                              value={newSPToEdit ? newSPToEdit : 0}
                              type="number"
                              className="h-7 text-xs  dark:border-[#4a4c4c] dark:bg-[#3a3c3c] dark:text-[#a9a093] text-center justify-center"
                            />
                          </div>
                        ) : (
                          <p>{item.SP}</p>
                        )}
                      </td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="accept-button dark:bg-[#3c6c80] dark:hover:brightness-[0.85] w-[60px] h-[30px]"
                          onClick={() => handleShowInputEdit(item)}
                        >
                          {personIdToEdit !== item.PersonID ? 'Edit' : 'Save'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <TableSkeleton rows={7} />
          )}
        </div>
        <div className="pt-8 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
          <button type="button" onClick={() => setOpen(false)} className="cancel-button">
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};
